import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Input, Space, Statistic, Table, Typography, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  adminGetGenerationActivity,
  AdminGenerationActivityResponse,
  AdminGenerationActivityRow,
} from '../../api/admin';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const GENERATION_LABELS: Record<string, string> = {
  AMAZON_CONTENT_GENERATION: 'Amazon',
  SHOPIFY_PRODUCT_GENERATION: 'Shopify Product',
  SHOPIFY_MEDIA_GENERATION: 'Shopify Media',
  BRAND_KEYWORD_RESEARCH: 'Brand Research',
  BRAND_KEYWORD_ASSIGNMENT: 'Keyword Assignment',
};

const DEFAULT_RANGE: [Dayjs, Dayjs] = [
  dayjs().subtract(29, 'day').startOf('day'),
  dayjs().endOf('day'),
];

const AdminGenerationsTab: React.FC = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(DEFAULT_RANGE);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AdminGenerationActivityResponse | null>(null);

  const loadActivity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminGetGenerationActivity({
        startDate: range[0].startOf('day').toISOString(),
        endDateExclusive: range[1].add(1, 'day').startOf('day').toISOString(),
      });
      setData(response);
    } catch (error) {
      notification.error({
        message: 'Failed to load generation activity',
        description: error instanceof Error ? error.message : String(error),
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void loadActivity();
  }, [loadActivity]);

  const filteredRows = useMemo(() => {
    const rows = data?.items ?? [];
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((row) => {
      const haystack = `${row.companyName} ${row.companyId}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [data?.items, searchTerm]);

  const columns = useMemo<ColumnsType<AdminGenerationActivityRow>>(
    () => [
      {
        title: 'Company',
        dataIndex: 'companyName',
        sorter: (a, b) => a.companyName.localeCompare(b.companyName),
        render: (_value, row) => (
          <Space direction="vertical" size={0}>
            <span>{row.companyName}</span>
            <Text type="secondary">{row.companyId}</Text>
          </Space>
        ),
      },
      {
        title: 'Total',
        dataIndex: 'totalCount',
        sorter: (a, b) => a.totalCount - b.totalCount,
      },
      {
        title: 'Successful',
        dataIndex: 'successfulCount',
        sorter: (a, b) => a.successfulCount - b.successfulCount,
      },
      {
        title: 'Failed',
        dataIndex: 'failedCount',
        sorter: (a, b) => a.failedCount - b.failedCount,
      },
      ...Object.entries(GENERATION_LABELS).map(([generationType, label]) => ({
        title: label,
        key: generationType,
        sorter: (a: AdminGenerationActivityRow, b: AdminGenerationActivityRow) =>
          (a.generationCounts[generationType] ?? 0) - (b.generationCounts[generationType] ?? 0),
        render: (_value: unknown, row: AdminGenerationActivityRow) => row.generationCounts[generationType] ?? 0,
      })),
    ],
    []
  );

  const totals = data?.totals;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card>
        <Space align="end" wrap>
          <div>
            <Text type="secondary">Timeframe</Text>
            <div>
              <RangePicker
                value={range}
                allowClear={false}
                onChange={(value) => {
                  if (!value || !value[0] || !value[1]) {
                    return;
                  }
                  setRange([value[0], value[1]]);
                }}
              />
            </div>
          </div>

          <Input
            placeholder="Search company"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={{ width: 240 }}
          />

          <Button icon={<ReloadOutlined />} onClick={() => void loadActivity()} loading={loading}>
            Refresh
          </Button>
        </Space>
      </Card>

      <Space size="middle" wrap>
        <Card>
          <Statistic title="Companies" value={totals?.companyCount ?? 0} loading={loading} />
        </Card>
        <Card>
          <Statistic title="Generations" value={totals?.totalCount ?? 0} loading={loading} />
        </Card>
        <Card>
          <Statistic title="Successful" value={totals?.successfulCount ?? 0} loading={loading} />
        </Card>
        <Card>
          <Statistic title="Failed" value={totals?.failedCount ?? 0} loading={loading} />
        </Card>
      </Space>

      <Card
        title="Generation Activity by Company"
        extra={
          <Text type="secondary">
            {range[0].format('DD MMM YYYY')} - {range[1].format('DD MMM YYYY')}
          </Text>
        }
      >
        <Table<AdminGenerationActivityRow>
          rowKey="companyId"
          columns={columns}
          dataSource={filteredRows}
          loading={loading}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          locale={{ emptyText: 'No generation activity found for the selected timeframe.' }}
          scroll={{ x: 900 }}
        />
      </Card>
    </Space>
  );
};

export default AdminGenerationsTab;
